import { DateTime, IANAZone } from "luxon";
import { SharedApiV2 } from "@capstone/mock-api";
import { TicketCountsPartner } from "src/app/core/ticket-counts/ticket-counts-partner.model";
import { TicketCountsSite } from "src/app/core/ticket-counts/ticket-counts-site.model";
import { DashboardTicketSite } from "src/app/partner/help-assist/dashboard-help-assist-site.model";
import {
  HelpAssistEventType,
  HelpAssistTicketStatus,
  HelpAssistTicketType,
} from "src/app/partner/help-assist/help-assist-enums";
import { ChangeItem } from "src/app/partner/help-assist/help-assist-event.model";
import { HelpAssistUser } from "src/app/partner/help-assist/help-assist-user.model";
import { HelpAssistTicketDock } from "src/app/partner/help-assist/models/help-assist-ticket-dock.model";
import {
  getHelpAssistTicketRouteUrl,
  HelpAssistTicketReference,
} from "src/app/partner/help-assist/models/ticket/base-help-assist-ticket.model";
import { DashboardHelpAssistTicketSchedule } from "src/app/partner/help-assist/models/ticket-schedule/dashboard-help-assist-ticket-schedule.model";
import { Brand, getEnumMember, parseDateTime, UUID } from "src/utils";
import { HelpAssistTicketUser } from "./help-assist-ticket-user.model";

export class DashboardHelpAssistTicket {
  private constructor(args: ClassProperties<DashboardHelpAssistTicket>) {
    this.assignedUser = args.assignedUser;
    this.category = args.category;
    this.createdOn = args.createdOn;
    this.dock = args.dock;
    this.emailAddress = args.emailAddress;
    this.hasNewComment = args.hasNewComment;
    this.id = args.id;
    this.isAutoApproved = args.isAutoApproved;
    this.isRead = args.isRead;
    this.lastEvent = args.lastEvent;
    this.lastEventCreatedOn = args.lastEventCreatedOn;
    this.orders = args.orders;
    this.organizationId = args.organizationId;
    this.organizationName = args.organizationName;
    this.ownerUser = args.ownerUser;
    this.phoneNumber = args.phoneNumber;
    this.schedule = args.schedule;
    this.site = args.site;
    this.status = args.status;
    this.type = args.type;
    this.updatedOn = args.updatedOn;
  }

  public readonly assignedUser: HelpAssistTicketUser | null;
  public readonly category: string | null;
  public readonly createdOn: DateTime;
  public readonly dock: HelpAssistTicketDock | null;
  public readonly emailAddress: string;
  public readonly hasNewComment: boolean;
  public readonly id: HelpAssistTicketReference["id"];
  public readonly isAutoApproved: boolean;
  public readonly isRead: boolean;
  public readonly lastEvent: DashboardHelpAssistEvent;
  public readonly lastEventCreatedOn: DateTime;
  public readonly orders: string | null;
  public readonly organizationId: string | null;
  public readonly organizationName: string | null;
  public readonly ownerUser: HelpAssistTicketUser;
  public readonly phoneNumber: string | null;
  public readonly schedule: DashboardHelpAssistTicketSchedule | null;
  public readonly site: DashboardTicketSite;
  public readonly status: HelpAssistTicketStatus;
  public readonly type: HelpAssistTicketType;
  public readonly updatedOn: DateTime;

  public static deserialize(
    apiModel: ApiDashboardHelpAssistTicket,
    { site }: DashboardHelpAssistTicketDeserializeArguments,
  ): DashboardHelpAssistTicket {
    const ownerUser = HelpAssistTicketUser.deserialize(apiModel.ownerUser);

    const schedule = apiModel.appointmentSchedule
      ? DashboardHelpAssistTicketSchedule.deserialize(
          apiModel.appointmentSchedule,
          {
            site,
          },
        )
      : null;

    return new DashboardHelpAssistTicket({
      assignedUser: apiModel.assignedUser
        ? HelpAssistTicketUser.deserialize(apiModel.assignedUser)
        : null,
      site,
      category: apiModel.category ?? null,
      createdOn: parseDateTime(apiModel.createdOn, site),
      dock: apiModel.dock
        ? HelpAssistTicketDock.deserialize(apiModel.dock)
        : null,
      hasNewComment: apiModel.hasNewComment,
      emailAddress: apiModel.emailAddress,
      id: apiModel.id,
      isAutoApproved: apiModel.isAutoApproved,
      isRead: apiModel.isRead,
      lastEvent: deserializeDashboardHelpAssistEvent(apiModel.lastEvent, {
        ownerUser,
      }),
      lastEventCreatedOn: parseDateTime(apiModel.lastEventCreatedOn, site),
      orders: apiModel.orders ?? null,
      organizationId: apiModel.organizationId ?? null,
      organizationName: apiModel.organizationName ?? null,
      ownerUser,
      phoneNumber: apiModel.phoneNumber ?? null,
      schedule,
      status: getEnumMember(HelpAssistTicketStatus, apiModel.status),
      type: getEnumMember(HelpAssistTicketType, apiModel.type),
      updatedOn: parseDateTime(apiModel.updatedOn, site),
    });
  }

  public static deserializeList(
    data: readonly ApiDashboardHelpAssistTicket[],
    args: DashboardHelpAssistTicketDeserializeArguments,
  ): readonly DashboardHelpAssistTicket[] {
    return data.map((ticket) =>
      DashboardHelpAssistTicket.deserialize(ticket, args),
    );
  }

  public static deserializeGlobalList(
    data: readonly ApiDashboardHelpAssistTicket[],
    {
      partners,
      sites,
      timeZone,
    }: GlobalDashboardHelpAssistTicketDeserializeArguments,
  ): readonly DashboardHelpAssistTicket[] {
    const sitesMap = new Map(sites.map((site) => [site.siteId, site]));
    const partnersMap = new Map(
      partners.map((partner) => [partner.partnerKey.toString(), partner]),
    );

    return data.map((apiTicket) => {
      const site = sitesMap.get(apiTicket.siteId);
      if (!site) {
        throw new Error(`Could not find site with ID "${apiTicket.siteId}".`);
      }

      const partner = partnersMap.get(apiTicket.partnerKey);
      if (!partner) {
        throw new Error(
          `Could not find partner with key "${apiTicket.partnerKey}".`,
        );
      }

      const dashboardTicketSite = {
        id: site.siteId,
        name: site.siteName,
        partner: {
          key: partner.partnerKey,
          name: partner.partnerName,
        },
        timeZone,
      };

      return DashboardHelpAssistTicket.deserialize(apiTicket, {
        site: dashboardTicketSite,
      });
    });
  }

  public getRouteUrl(...childPaths: string[]): string {
    return getHelpAssistTicketRouteUrl(this, ...childPaths);
  }
}

interface DashboardHelpAssistTicketDeserializeArguments {
  readonly site: DashboardTicketSite;
}

interface GlobalDashboardHelpAssistTicketDeserializeArguments {
  partners: readonly TicketCountsPartner[];
  sites: TicketCountsSite[];
  timeZone: IANAZone;
}

type ApiDashboardHelpAssistTicket = SharedApiV2.TicketListDto;

export function deserializeDashboardHelpAssistEvent(
  apiModel: SharedApiV2.TicketEventDto,
  { ownerUser }: DashboardHelpAssistEventDeserializeArgs,
): DashboardHelpAssistEvent {
  return {
    changes: apiModel.changes && ChangeItem.deserializeList(apiModel.changes),
    comment: apiModel.comment ?? null,
    createdOn: parseDateTime(apiModel.createdOn),
    emailId: apiModel.emailId ? new UUID(apiModel.emailId) : null,
    id: apiModel.id,
    ownerUser,
    type: getEnumMember(HelpAssistEventType, apiModel.type),
    user: HelpAssistTicketUser.deserialize(apiModel.user),
  };
}

export interface DashboardHelpAssistEvent {
  readonly id: Brand<number, "help-assist-event">;
  readonly changes: readonly ChangeItem[] | null;
  readonly comment: string | null;
  readonly createdOn: DateTime;
  readonly emailId: UUID | null;
  readonly ownerUser: HelpAssistUser;
  readonly type: HelpAssistEventType;
  readonly user: HelpAssistUser;
}

interface DashboardHelpAssistEventDeserializeArgs {
  ownerUser: HelpAssistUser;
}
